import type { EntityType } from "./canonical";
import type { FieldMapping, MappingSpec } from "./mapping";

/**
 * Migration kits: a source format plus a target, with a pre-filled, editable mapping per entity (ENG-1, KLV-7, SHP-6).
 * Column names for third-party exports follow each vendor's standard export. Users can edit any preset
 * in the mapping step, because real exports vary by account configuration.
 */

export type SourceKind = "csv" | "mailchimp" | "shopify";
export type TargetKind = "klaviyo" | "shopify";

export interface KitEntity {
  entity: EntityType;
  label: string;
  mapping: MappingSpec;
  /** Columns we expect in the source file, used to auto-detect the kit and to warn about missing columns. */
  expectedColumns: string[];
  notes?: string;
}

export interface Kit {
  id: string;
  name: string;
  description: string;
  source: SourceKind;
  target: TargetKind;
  entities: KitEntity[];
}

const f = (target: string, source?: string | string[], transforms: FieldMapping["transforms"] = []): FieldMapping =>
  source === undefined ? { target, transforms } : { target, source, transforms };

const trimLower = [{ op: "trim" as const }, { op: "lower" as const }];

// ---------------------------------------------------------------------------------------------
// → Klaviyo (profiles)
// ---------------------------------------------------------------------------------------------

const genericProfiles: KitEntity = {
  entity: "contact",
  label: "Profiles",
  expectedColumns: ["email", "first_name", "last_name", "phone", "email_consent", "email_consent_at"],
  mapping: {
    entity: "contact",
    keyField: "email",
    requireKey: true,
    fields: [
      f("email", ["email", "Email", "Email Address", "E-mail"], trimLower),
      f("first_name", ["first_name", "First Name", "firstname", "FNAME"], [{ op: "trim" }]),
      f("last_name", ["last_name", "Last Name", "lastname", "LNAME"], [{ op: "trim" }]),
      f("phone", ["phone", "Phone", "phone_number", "Phone Number"], [{ op: "phoneE164", defaultCountry: "US" }]),
      f("city", ["city", "City"]),
      f("country", ["country", "Country"]),
      f("consent.email.status", ["email_consent", "Email Consent", "status", "Status"]),
      f("consent.email.at", ["email_consent_at", "Consent Date", "Opt-in Date"], [{ op: "date", input: "auto" }]),
      f("lists", ["lists", "Lists", "tags", "Tags"], [{ op: "list", separator: "," }]),
    ],
  },
};

const mailchimpProfiles: KitEntity = {
  entity: "contact",
  label: "Audience members",
  notes: "Read via the Mailchimp Marketing API. 'cleaned' members become suppressions; 'unsubscribed' stays unsubscribed.",
  expectedColumns: ["email_address", "status", "timestamp_opt", "merge_fields.FNAME", "merge_fields.LNAME", "tags"],
  mapping: {
    entity: "contact",
    keyField: "email",
    requireKey: true,
    fields: [
      f("email", "email_address", trimLower),
      f("first_name", "merge_fields.FNAME", [{ op: "trim" }]),
      f("last_name", "merge_fields.LNAME", [{ op: "trim" }]),
      f("phone", "merge_fields.PHONE", [{ op: "phoneE164", defaultCountry: "US" }]),
      f("address1", "merge_fields.ADDRESS.addr1"),
      f("city", "merge_fields.ADDRESS.city"),
      f("region", "merge_fields.ADDRESS.state"),
      f("zip", "merge_fields.ADDRESS.zip"),
      f("country", "merge_fields.ADDRESS.country"),
      f("locale", "language"),
      f("consent.email.status", "status"),
      f("consent.email.at", ["timestamp_opt", "timestamp_signup"], [{ op: "date", input: "auto" }]),
      f("consent.email.method", undefined, [{ op: "default", value: "import:mailchimp" }]),
      f("consent.email.reason", "unsubscribe_reason"),
      f("custom.mailchimp_vip", "vip", [{ op: "boolean" }]),
      f("custom.mailchimp_member_rating", "member_rating", [{ op: "number" }]),
      f("lists", ["list_name", "tags"], [{ op: "list", separator: "," }]),
    ],
  },
};

const sfmcProfiles: KitEntity = {
  entity: "contact",
  label: "All Subscribers export",
  notes: "SFMC 'Held' and 'Bounced' become suppressions. Custom attributes can be mapped to custom.* fields.",
  expectedColumns: ["Email Address", "Subscriber Key", "Status", "Date Joined", "Date Unsubscribed"],
  mapping: {
    entity: "contact",
    keyField: "email",
    requireKey: true,
    fields: [
      f("email", "Email Address", trimLower),
      f("custom.sfmc_subscriber_key", "Subscriber Key"),
      f("first_name", ["First Name", "FirstName"], [{ op: "trim" }]),
      f("last_name", ["Last Name", "LastName"], [{ op: "trim" }]),
      f("consent.email.status", "Status"),
      f("consent.email.at", ["Date Unsubscribed", "Date Joined"], [{ op: "date", input: "auto" }]),
      f("consent.email.method", undefined, [{ op: "default", value: "import:sfmc" }]),
      f("created_at", "Date Joined", [{ op: "date", input: "auto" }]),
    ],
  },
};

const omnisendProfiles: KitEntity = {
  entity: "contact",
  label: "Contacts export",
  notes: "Omnisend exports email and SMS status separately; both are preserved.",
  expectedColumns: ["email", "phone", "firstName", "lastName", "emailStatus", "smsStatus", "createdAt", "tags"],
  mapping: {
    entity: "contact",
    keyField: "email",
    requireKey: true,
    fields: [
      f("email", ["email", "Email"], trimLower),
      f("phone", ["phone", "Phone"], [{ op: "phoneE164", defaultCountry: "US" }]),
      f("first_name", ["firstName", "First name"], [{ op: "trim" }]),
      f("last_name", ["lastName", "Last name"], [{ op: "trim" }]),
      f("consent.email.status", ["emailStatus", "Email status", "status"]),
      f("consent.email.method", undefined, [{ op: "default", value: "import:omnisend" }]),
      f("consent.sms.status", ["smsStatus", "SMS status"]),
      f("consent.sms.method", undefined, [{ op: "default", value: "import:omnisend" }]),
      f("created_at", ["createdAt", "Created at"], [{ op: "date", input: "auto" }]),
      f("lists", ["tags", "Tags"], [{ op: "list", separator: "," }]),
    ],
  },
};

const attentiveSubscribers: KitEntity = {
  entity: "contact",
  label: "Subscribers export",
  notes:
    "SMS consent needs an opt-in timestamp. Rows without one are imported with SMS 'never_subscribed' and listed in the dry run, to avoid TCPA exposure.",
  expectedColumns: ["Phone", "Email", "Subscriber Status", "Join Date", "Opt Out Date"],
  mapping: {
    entity: "contact",
    keyField: "phone",
    requireKey: true,
    fields: [
      f("phone", ["Phone", "phone", "Phone Number"], [{ op: "phoneE164", defaultCountry: "US" }]),
      f("email", ["Email", "email"], trimLower),
      f("consent.sms.status", ["Subscriber Status", "Status"]),
      f("consent.sms.at", ["Opt Out Date", "Join Date"], [{ op: "date", input: "auto" }]),
      f("consent.sms.method", undefined, [{ op: "default", value: "import:attentive" }]),
    ],
  },
};

const historicalEvents: KitEntity = {
  entity: "event",
  label: "Historical events (orders, opens, clicks…)",
  notes: "Backfilled with their original timestamps and without triggering flows. Klaviyo events can't be deleted, so they're not revertible; the dry run says so before anything is written.",
  expectedColumns: ["email", "metric", "time", "value", "unique_id"],
  mapping: {
    entity: "event",
    keyField: "unique_id",
    requireKey: true,
    fields: [
      f("unique_id", ["unique_id", "event_id", "Event ID", "order_id", "Order ID"]),
      f("email", ["email", "Email", "Customer Email"], trimLower),
      f("metric", ["metric", "Metric", "event", "Event Name"], [{ op: "trim" }, { op: "default", value: "Placed Order" }]),
      f("time", ["time", "timestamp", "Date", "Order Date", "created_at"], [{ op: "date", input: "auto" }]),
      f("value", ["value", "Value", "revenue", "Total"], [{ op: "number" }]),
      f("properties", ["properties"]),
      f("property.order_number", ["order_number", "Order Number"]),
      f("property.items", ["items", "Items", "products"]),
      f("property.currency", ["currency", "Currency"], [{ op: "upper" }]),
    ],
  },
};

// ---------------------------------------------------------------------------------------------
// → Shopify
// ---------------------------------------------------------------------------------------------

const genericProducts: KitEntity = {
  entity: "product",
  label: "Products",
  expectedColumns: ["handle", "title", "sku", "price", "description_html", "vendor", "tags", "image_urls"],
  mapping: {
    entity: "product",
    keyField: "handle",
    requireKey: true,
    fields: [
      f("handle", ["handle", "Handle", "url_key"], [{ op: "trim" }, { op: "lower" }, { op: "regexReplace", pattern: "[^a-z0-9-]+", replacement: "-" }]),
      f("title", ["title", "Title", "name"]),
      f("sku", ["sku", "SKU", "Variant SKU"]),
      f("price", ["price", "Price", "Variant Price"], [{ op: "number" }]),
      f("compare_at_price", ["compare_at_price", "Compare At Price"], [{ op: "number" }]),
      f("description_html", ["description_html", "Body (HTML)", "description"]),
      f("vendor", ["vendor", "Vendor"]),
      f("product_type", ["product_type", "Type"]),
      f("tags", ["tags", "Tags"], [{ op: "list", separator: "," }]),
      f("inventory_quantity", ["inventory_quantity", "Variant Inventory Qty", "qty"], [{ op: "number" }]),
      f("image_urls", ["image_urls", "Image Src"], [{ op: "list", separator: "," }]),
      f("status", ["status", "Status"], [{ op: "lower" }, { op: "default", value: "active" }]),
    ],
  },
};

const genericCustomers: KitEntity = {
  entity: "contact",
  label: "Customers",
  expectedColumns: ["email", "first_name", "last_name", "phone", "accepts_marketing", "tags"],
  mapping: {
    entity: "contact",
    keyField: "email",
    requireKey: true,
    fields: [
      f("email", ["email", "Email"], trimLower),
      f("first_name", ["first_name", "First Name"]),
      f("last_name", ["last_name", "Last Name"]),
      f("phone", ["phone", "Phone"], [{ op: "phoneE164", defaultCountry: "US" }]),
      f("address1", ["address1", "Default Address Address1"]),
      f("city", ["city", "Default Address City"]),
      f("region", ["region", "Default Address Province Code"]),
      f("zip", ["zip", "Default Address Zip"]),
      f("country", ["country", "Default Address Country Code"]),
      f("consent.email.status", ["accepts_marketing", "Accepts Email Marketing"], [{ op: "valueMap", map: { yes: "subscribed", no: "never_subscribed", true: "subscribed", false: "never_subscribed" }, fallback: "keep" }]),
      f("tags", ["tags", "Tags"], [{ op: "list", separator: "," }]),
      f("notes", ["notes", "Note"]),
    ],
  },
};

const genericOrders: KitEntity = {
  entity: "order",
  label: "Orders (one row per line item)",
  notes: "Imported as historical orders: no customer notifications and no inventory changes.",
  expectedColumns: ["order_number", "email", "created_at", "currency", "sku", "quantity", "price", "title"],
  mapping: {
    entity: "order",
    keyField: "order_number",
    requireKey: true,
    fields: [
      f("order_number", ["order_number", "Order ID", "Name"]),
      f("email", ["email", "Customer Email", "Email"], trimLower),
      f("created_at", ["created_at", "Order Date", "Created at"], [{ op: "date", input: "auto" }]),
      f("currency", ["currency", "Currency"], [{ op: "upper" }, { op: "default", value: "USD" }]),
      f("financial_status", ["financial_status", "Financial Status"], [{ op: "lower" }, { op: "default", value: "paid" }]),
      f("fulfillment_status", ["fulfillment_status", "Fulfillment Status"], [{ op: "lower" }]),
      f("line_item.sku", ["sku", "Lineitem sku", "SKU"]),
      f("line_item.title", ["title", "Lineitem name", "Product Name"]),
      f("line_item.quantity", ["quantity", "Lineitem quantity", "Quantity"], [{ op: "number" }]),
      f("line_item.price", ["price", "Lineitem price", "Price"], [{ op: "number" }]),
    ],
  },
};

const genericRedirects: KitEntity = {
  entity: "redirect",
  label: "URL redirects",
  expectedColumns: ["path", "target"],
  mapping: {
    entity: "redirect",
    keyField: "path",
    requireKey: true,
    fields: [
      f("path", ["path", "Redirect from", "old_url", "from"], [{ op: "trim" }, { op: "regexReplace", pattern: "^https?://[^/]+", replacement: "" }]),
      f("target", ["target", "Redirect to", "new_url", "to"], [{ op: "trim" }]),
    ],
  },
};

const bigcommerceProducts: KitEntity = {
  ...genericProducts,
  label: "BigCommerce products export",
  expectedColumns: ["Product Name", "Product Code/SKU", "Price", "Product Description", "Brand Name", "Current Stock Level", "Product URL"],
  mapping: {
    entity: "product",
    keyField: "handle",
    requireKey: true,
    fields: [
      f("handle", ["Product URL", "Product Name"], [{ op: "trim" }, { op: "lower" }, { op: "regexReplace", pattern: "^/|/$", replacement: "" }, { op: "regexReplace", pattern: "[^a-z0-9-]+", replacement: "-" }]),
      f("title", "Product Name"),
      f("sku", "Product Code/SKU"),
      f("price", "Price", [{ op: "number" }]),
      f("compare_at_price", "Retail Price", [{ op: "number" }]),
      f("description_html", "Product Description"),
      f("vendor", "Brand Name"),
      f("inventory_quantity", "Current Stock Level", [{ op: "number" }]),
      f("weight", "Product Weight", [{ op: "number" }]),
      f("image_urls", ["Product Image URL - 1", "Product Image File - 1"], [{ op: "list", separator: "," }]),
      f("tags", "Category", [{ op: "list", separator: ";" }]),
    ],
  },
};

const woocommerceProducts: KitEntity = {
  ...genericProducts,
  label: "WooCommerce products export",
  expectedColumns: ["ID", "Type", "SKU", "Name", "Description", "Regular price", "Sale price", "Categories", "Tags", "Images", "Stock"],
  mapping: {
    entity: "product",
    keyField: "handle",
    requireKey: true,
    fields: [
      f("handle", ["Slug", "Name"], [{ op: "trim" }, { op: "lower" }, { op: "regexReplace", pattern: "[^a-z0-9-]+", replacement: "-" }]),
      f("title", "Name"),
      f("sku", "SKU"),
      f("price", ["Sale price", "Regular price"], [{ op: "number" }]),
      f("compare_at_price", "Regular price", [{ op: "number" }]),
      f("description_html", ["Description", "Short description"]),
      f("tags", ["Tags", "Categories"], [{ op: "list", separator: "," }]),
      f("image_urls", "Images", [{ op: "list", separator: "," }]),
      f("inventory_quantity", "Stock", [{ op: "number" }]),
      f("weight", "Weight (kg)", [{ op: "number" }]),
      f("status", "Published", [{ op: "valueMap", map: { "1": "active", "0": "draft", "-1": "draft" }, fallback: "keep" }]),
      f("parent_handle", "Parent"),
      f("option1_name", "Attribute 1 name"),
      f("option1_value", "Attribute 1 value(s)"),
    ],
  },
};

const magentoProducts: KitEntity = {
  ...genericProducts,
  label: "Magento / Adobe Commerce products export",
  expectedColumns: ["sku", "name", "description", "price", "special_price", "qty", "url_key", "base_image", "meta_title"],
  mapping: {
    entity: "product",
    keyField: "handle",
    requireKey: true,
    fields: [
      f("handle", ["url_key", "sku"], [{ op: "trim" }, { op: "lower" }, { op: "regexReplace", pattern: "[^a-z0-9-]+", replacement: "-" }]),
      f("title", "name"),
      f("sku", "sku"),
      f("price", ["special_price", "price"], [{ op: "number" }]),
      f("compare_at_price", "price", [{ op: "number" }]),
      f("description_html", "description"),
      f("inventory_quantity", "qty", [{ op: "number" }]),
      f("weight", "weight", [{ op: "number" }]),
      f("image_urls", ["base_image", "additional_images"], [{ op: "concat", separator: "," }, { op: "list", separator: "," }]),
      f("seo_title", "meta_title"),
      f("seo_description", "meta_description"),
      f("product_type", "attribute_set_code"),
    ],
  },
};

const magentoCustomers: KitEntity = {
  ...genericCustomers,
  label: "Magento customers export",
  notes: "Magento password hashes can't be migrated. Customers receive Shopify account invites instead (optional).",
  expectedColumns: ["email", "firstname", "lastname", "created_at"],
  mapping: {
    entity: "contact",
    keyField: "email",
    requireKey: true,
    fields: [
      f("email", "email", trimLower),
      f("first_name", "firstname"),
      f("last_name", "lastname"),
      f("created_at", "created_at", [{ op: "date", input: "auto" }]),
      f("phone", "_address_telephone", [{ op: "phoneE164", defaultCountry: "US" }]),
      f("city", "_address_city"),
      f("zip", "_address_postcode"),
      f("country", "_address_country_id"),
    ],
  },
};

const bigcommerceCustomers: KitEntity = {
  ...genericCustomers,
  label: "BigCommerce customers export",
  expectedColumns: ["Email", "First Name", "Last Name", "Phone", "Company", "Date Joined", "Receive Review/Marketing Emails"],
  mapping: {
    entity: "contact",
    keyField: "email",
    requireKey: true,
    fields: [
      f("email", "Email", trimLower),
      f("first_name", "First Name"),
      f("last_name", "Last Name"),
      f("phone", "Phone", [{ op: "phoneE164", defaultCountry: "US" }]),
      f("organization", "Company"),
      f("created_at", "Date Joined", [{ op: "date", input: "auto" }]),
      f("consent.email.status", "Receive Review/Marketing Emails", [{ op: "valueMap", map: { Y: "subscribed", N: "never_subscribed", Yes: "subscribed", No: "never_subscribed" }, fallback: "keep" }]),
    ],
  },
};

const woocommerceCustomers: KitEntity = {
  ...genericCustomers,
  label: "WooCommerce customers export",
  expectedColumns: ["email", "first_name", "last_name", "billing_phone", "billing_city", "billing_country"],
  mapping: {
    entity: "contact",
    keyField: "email",
    requireKey: true,
    fields: [
      f("email", ["email", "user_email"], trimLower),
      f("first_name", ["first_name", "billing_first_name"]),
      f("last_name", ["last_name", "billing_last_name"]),
      f("phone", "billing_phone", [{ op: "phoneE164", defaultCountry: "US" }]),
      f("address1", "billing_address_1"),
      f("city", "billing_city"),
      f("region", "billing_state"),
      f("zip", "billing_postcode"),
      f("country", "billing_country"),
    ],
  },
};

export const KITS: Kit[] = [
  { id: "csv-to-klaviyo", name: "CSV → Klaviyo", description: "Import any profile CSV with consent preserved, plus historical events.", source: "csv", target: "klaviyo", entities: [genericProfiles, historicalEvents] },
  { id: "mailchimp-to-klaviyo", name: "Mailchimp → Klaviyo", description: "Audiences, members, tags and consent, read straight from the Mailchimp API.", source: "mailchimp", target: "klaviyo", entities: [mailchimpProfiles] },
  { id: "sfmc-to-klaviyo", name: "Salesforce Marketing Cloud → Klaviyo", description: "From an All Subscribers or data extension export. Keeps unsubscribes and bounces suppressed.", source: "csv", target: "klaviyo", entities: [sfmcProfiles] },
  { id: "omnisend-to-klaviyo", name: "Omnisend → Klaviyo", description: "Email and SMS consent from an Omnisend contacts export.", source: "csv", target: "klaviyo", entities: [omnisendProfiles] },
  { id: "attentive-to-klaviyo", name: "Attentive → Klaviyo", description: "SMS subscribers with opt-in timestamps from an Attentive export.", source: "csv", target: "klaviyo", entities: [attentiveSubscribers] },
  { id: "csv-to-shopify", name: "CSV → Shopify", description: "Products, customers, orders and redirects from CSV.", source: "csv", target: "shopify", entities: [genericProducts, genericCustomers, genericOrders, genericRedirects] },
  { id: "bigcommerce-to-shopify", name: "BigCommerce → Shopify", description: "Products, customers and orders from BigCommerce exports, plus redirects.", source: "csv", target: "shopify", entities: [bigcommerceProducts, bigcommerceCustomers, genericOrders, genericRedirects] },
  { id: "woocommerce-to-shopify", name: "WooCommerce → Shopify", description: "Products, customers and orders from WooCommerce exports, plus redirects.", source: "csv", target: "shopify", entities: [woocommerceProducts, woocommerceCustomers, genericOrders, genericRedirects] },
  { id: "magento-to-shopify", name: "Magento → Shopify", description: "Products, customers and orders from Magento / Adobe Commerce exports, plus redirects.", source: "csv", target: "shopify", entities: [magentoProducts, magentoCustomers, genericOrders, genericRedirects] },
  { id: "shopify-bulk-edit", name: "Shopify bulk edit with undo", description: "Export products or customers, edit the CSV, preview the diff, apply, and undo if needed.", source: "csv", target: "shopify", entities: [genericProducts, genericCustomers] },
];

export function getKit(id: string): Kit | undefined {
  return KITS.find((k) => k.id === id);
}

/** Score how well a CSV header row matches each kit entity. */
export function suggestKits(headers: string[]): Array<{ kitId: string; entity: EntityType; score: number }> {
  const have = new Set(headers.map((h) => h.trim().toLowerCase()));
  const out: Array<{ kitId: string; entity: EntityType; score: number }> = [];
  for (const kit of KITS) {
    if (kit.source !== "csv") continue;
    for (const e of kit.entities) {
      const hits = e.expectedColumns.filter((c) => have.has(c.toLowerCase())).length;
      if (hits) out.push({ kitId: kit.id, entity: e.entity, score: hits / e.expectedColumns.length });
    }
  }
  return out.sort((a, b) => b.score - a.score);
}
